/**
 * Cara sebuah prospek disebut di layar: namanya, datanya, dan meetingnya. Dipakai kedua
 * daftar prospek dan detailnya, supaya semuanya menyebut hal yang sama dengan kata yang sama.
 * Di daftar mana seseorang berada dan di tahap apa: `daftar.js`.
 */
const larik = (x) => (Array.isArray(x) ? x : [])

/** Nama yang ditampilkan: nama usahanya, lalu nama akunnya. */
export function namaProspek(p, t) {
  if (p.company_name) return p.company_name
  if (p.sender_username) return /\s/.test(p.sender_username) ? p.sender_username : `@${p.sender_username}`
  return t('prospek.tanpaNama')
}

const KUNCI_BAWAAN = ['company_name', 'company_address', 'pic_name', 'pic_email', 'pic_phone']

/**
 * Baris data sebuah prospek: pertanyaan TENANT INI beserta isiannya (`fields`). Tenant yang
 * belum punya satu pun definisi pertanyaan tetap melihat lima kolom bawaannya.
 */
export function barisData(p, t) {
  if (p.fields?.length) return p.fields
  return [
    ['company_name', 'prospek.perusahaan'], ['company_address', 'prospek.alamat'],
    ['pic_name', 'prospek.pic'], ['pic_email', 'prospek.email'], ['pic_phone', 'prospek.telepon'],
  ].map(([kunci, k]) => ({ kunci, label: t(k), nilai: p[kunci] || null, wajib: true, aktif: true }))
}

/**
 * Isian formulir data jadi badan PATCH: hanya yang berubah. Lima kolom bawaan dikirim
 * dengan namanya (kosong berarti null); pertanyaan tambahan tenant lewat `custom_fields`,
 * yang digabung backend, bukan ditimpa.
 */
export function perubahanData(isi, semula) {
  const badan = {}
  const tambahan = {}
  for (const [k, v] of Object.entries(isi)) {
    const bersih = (v ?? '').trim()
    if (bersih === (semula[k] ?? '')) continue
    if (KUNCI_BAWAAN.includes(k)) badan[k] = bersih || null
    else tambahan[k] = bersih
  }
  if (Object.keys(tambahan).length) badan.custom_fields = tambahan
  return badan
}

/**
 * Keadaan satu meeting di daftar meeting sebuah prospek: `terjadwal`, `tanpaTanggal`,
 * `perluDitutup` (jamnya sudah lewat dan belum ada yang menyatakan hasilnya), `selesai`,
 * `tidakHadir`, atau `batal`.
 */
export function keadaanMeeting(m, kini = new Date()) {
  if (m.status === 'done') return 'selesai'
  if (m.status === 'no_show') return 'tidakHadir'
  if (m.status === 'cancelled') return 'batal'
  if (!m.confirmed_at) return 'tanpaTanggal'
  return new Date(m.confirmed_at).getTime() < kini.getTime() ? 'perluDitutup' : 'terjadwal'
}

/** Yang masih menuntut sesuatu dari tim, jadi tampil terbuka di daftarnya. */
export const MEETING_TERBUKA = ['tanpaTanggal', 'terjadwal', 'perluDitutup']

/**
 * Meeting satu prospek untuk daftarnya, `[{ m, keadaan, ke }]`: yang belum bertanggal, yang
 * akan datang (terdekat dulu), yang sudah lewat dan belum ditutup, lalu yang sudah ditutup
 * (terbaru dulu). `ke` nomor urutnya menurut waktu: meeting pertama, kedua, dan seterusnya;
 * yang belum bertanggal dihitung paling akhir. Yang dibatalkan tidak pernah berlangsung, jadi
 * tidak bernomor (`ke: null`): membatalkan satu meeting tidak menjadikan yang berikutnya "ke-3".
 */
export function susunMeeting(meetings, kini = new Date()) {
  const waktu = (m) => (m.confirmed_at ? new Date(m.confirmed_at).getTime() : Infinity)
  let nomor = 0
  const baris = [...larik(meetings)].sort((x, y) => waktu(x) - waktu(y))
    .map((m) => ({ m, keadaan: keadaanMeeting(m, kini), ke: m.status === 'cancelled' ? null : (nomor += 1) }))
  const kelompok = (k) => (k === 'tanpaTanggal' ? 0 : k === 'terjadwal' ? 1 : k === 'perluDitutup' ? 2 : 3)
  return baris.sort((x, y) => kelompok(x.keadaan) - kelompok(y.keadaan)
    // Yang akan datang: terdekat dulu. Yang sudah lewat: terbaru dulu.
    || (x.keadaan === 'terjadwal' ? waktu(x.m) - waktu(y.m) : waktu(y.m) - waktu(x.m)))
}

/**
 * Posisi sebuah prospek dalam tiga hal: meeting terakhir yang sudah lewat (yang dibatalkan
 * tidak dihitung), meeting berikutnya, dan banyaknya meeting.
 */
export function posisiProspek(p, kini = new Date()) {
  const susun = susunMeeting(p.meetings, kini)
  const lewat = susun.filter((x) => ['perluDitutup', 'selesai', 'tidakHadir'].includes(x.keadaan))
    .sort((x, y) => new Date(y.m.confirmed_at) - new Date(x.m.confirmed_at))
  return {
    terakhir: lewat[0] ?? null,
    berikutnya: susun.find((x) => x.keadaan === 'terjadwal') ?? null,
    menunggu: susun.find((x) => x.keadaan === 'tanpaTanggal') ?? null,
    jumlah: susun.length,
  }
}

/**
 * Label data DASAR yang masih kosong. Backend menolak "Jadwalkan meeting" selama salah satu
 * dari kelimanya kosong, jadi kartu Meeting menyebutnya lebih dulu daripada membiarkan
 * orangnya mengisi formulir lalu ditolak.
 */
export function dataDasarKurang(p, t) {
  const label = Object.fromEntries(barisData({ ...p, fields: [] }, t).map((b) => [b.kunci, b.label]))
  for (const f of larik(p.fields)) if (KUNCI_BAWAAN.includes(f.kunci)) label[f.kunci] = f.label
  return KUNCI_BAWAAN.filter((k) => !p[k]).map((k) => label[k])
}
